/**
 * In-browser timeline export. Consumes the same request body as the server export
 * (`/export-video`) and renders every frame on a canvas — framing, clip animation
 * (preset + beat-sync), filters, transitions, VFX, host overlays and burned-in subtitles —
 * then encodes H.264/AAC MP4 with WebCodecs (mediabunny). No upload, no server job.
 */
import {
  AudioBufferSource,
  BufferTarget,
  CanvasSource,
  Mp4OutputFormat,
  Output,
  QUALITY_HIGH,
  QUALITY_MEDIUM,
  QUALITY_VERY_HIGH,
  StreamTarget,
  getFirstEncodableAudioCodec,
  getFirstEncodableVideoCodec,
} from 'mediabunny';
import {
  applyTransformOps,
  coverFitRect,
  drawStyledLayer,
  fadeFilterAlpha,
  motionOps,
  paintCssBackground,
} from '@/lib/localExport/canvasStyle';
import { buildCaptionSpec, drawCaption, preloadCaptionFonts } from '@/lib/localExport/captionRender';
import { clipMotionAt } from '@/lib/localExport/clipMotion';
import { cropBoxPixels, expandTimelineClip, rectAt } from '@/lib/dialogueShots';
import {
  ChunkedAudioMixer,
  MIX_CHANNELS,
  MIX_SAMPLE_RATE,
  audioPiecesForExport,
  createAudioDecoder,
} from '@/lib/localExport/audioMix';
import { MediaCache, openClipSource } from '@/lib/localExport/mediaSources';
import {
  EXPORT_FPS,
  buildTrackPlans,
  clipFirstNeededFrame,
  clipFrameCount,
  clipLastNeededFrame,
  clipPlaybackEndTime,
  outputFrameCount,
  resolveFrame,
} from '@/lib/localExport/timelinePlan';
import { layerToOverlayBox } from '@/lib/videoEditorHostOverlays';
import { exportTargetSizeForAspect } from '@/lib/videoExportFraming';
import { buildFilterOverlayLayers } from '@/lib/videoFilterOverlays';
import { cssPreviewForFilters, normalizeClipFilters } from '@/lib/videoFilters';
import { transitionPreviewLayerStyles } from '@/lib/videoTransitionPreview';
import { RenderGraph } from '@/vfx/renderGraph';
import { deserializeEffectClip } from '@/vfx/serialization';

export function localExportSupported() {
  return typeof window !== 'undefined'
    && typeof window.VideoEncoder === 'function'
    && typeof window.OfflineAudioContext === 'function';
}

/**
 * Ask where to save before rendering (must run inside the click handler). Returns a
 * file handle, or null when the browser has no save picker (the file is downloaded instead).
 */
export async function pickLocalExportFile(filename) {
  if (typeof window === 'undefined' || typeof window.showSaveFilePicker !== 'function') return null;
  return window.showSaveFilePicker({
    suggestedName: `${filename}.mp4`,
    types: [{ description: 'MP4 video', accept: { 'video/mp4': ['.mp4'] } }],
  });
}

function qualityFor(exportQuality) {
  const q = String(exportQuality || 'best').toLowerCase();
  if (q === 'lossless') return QUALITY_VERY_HIGH;
  if (q === 'low') return QUALITY_MEDIUM;
  return QUALITY_HIGH;
}

function makeCanvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/**
 * mediabunny closes its stream on cancel, which would commit a truncated MP4 to disk.
 * Route writes through a proxy so a cancelled/failed export discards the file instead.
 */
function discardableFileStream(fileWritable) {
  let discarded = false;
  const stream = new WritableStream({
    write: (chunk) => fileWritable.write(chunk),
    close: () => (discarded ? undefined : fileWritable.close()),
    abort: () => fileWritable.abort(),
  });
  const discard = async () => {
    discarded = true;
    await fileWritable.abort().catch(() => {});
  };
  return { stream, discard };
}

function abortError() {
  const err = new Error('Local export cancelled');
  err.name = 'AbortError';
  return err;
}

function timelineItems(body) {
  const animSpeed = body.editor_animation_speed;
  return [...(body.clips || [])]
    .filter((c) => c && (c.video_url || c.image_url))
    .sort((a, b) => (Number(a.start_time) || 0) - (Number(b.start_time) || 0))
    .flatMap(expandTimelineClip)
    .map((c) => (animSpeed != null && c.editor_animation_speed == null
      ? { ...c, editor_animation_speed: animSpeed }
      : c));
}

/** Subtitle cues with precomputed layout, visible only where a clip covers the timeline. */
function captionCues(body, items, w, h, aspect) {
  const style = body.subtitle_style || {};
  const cover = items.map((it) => [Number(it.start_time) || 0, clipPlaybackEndTime(it)]);
  const covered = (t) => cover.some(([a, b]) => t >= a && t < b);
  return (body.subtitle_clips || [])
    .filter((c) => c && !c.muted && String(c.text || '').trim())
    .map((c) => {
      const start = Number(c.start_time) || 0;
      return {
        start,
        end: start + Math.max(0.01, Number(c.playback_seconds) || 0.01),
        spec: buildCaptionSpec(c, c.style || style, w, h, aspect),
        covered,
      };
    });
}

class ClipPainter {
  constructor(items, counts, w, h, fps, framing) {
    this.items = items;
    this.counts = counts;
    this.w = w;
    this.h = h;
    this.fps = fps;
    this.framing = framing;
    this.looks = items.map((item) => {
      const filters = normalizeClipFilters(item.video_filters);
      return {
        filters,
        css: cssPreviewForFilters(filters) || 'none',
        overlays: buildFilterOverlayLayers(filters),
        fades: filters.filter((f) => f.id === 'fade_in' || f.id === 'fade_out'),
      };
    });
  }

  paint(ctx, key, j, frame) {
    const { w, h } = this;
    ctx.save();
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
    if (!frame) return;
    const item = this.items[key];
    const look = this.looks[key];
    const count = this.counts[key];
    const focusKeys = !item.video_url ? item.focus_camera?.keyframes : null;
    if (focusKeys?.length) {
      const crop = cropBoxPixels(rectAt(focusKeys, j / this.fps), frame.width, frame.height, w / h);
      ctx.save();
      ctx.filter = look.css;
      ctx.drawImage(frame.image, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, w, h);
      ctx.restore();
    } else {
      const rect = coverFitRect(frame.width, frame.height, w, h, this.framing);
      if (!rect) return;
      ctx.save();
      ctx.filter = look.css;
      const motion = clipMotionAt(item, j, count, this.fps, w, h);
      if (motion) applyTransformOps(ctx, motionOps(motion, w, h), w, h);
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.clip();
      ctx.drawImage(frame.image, rect.dx, rect.dy, rect.dw, rect.dh);
      ctx.restore();
    }

    for (const layer of look.overlays) paintCssBackground(ctx, layer.style?.background, w, h);
    for (const f of look.fades) {
      const alpha = fadeFilterAlpha(f.id, f.intensity, j / Math.max(1, count - 1));
      if (alpha < 0.999) {
        ctx.save();
        ctx.globalAlpha = 1 - alpha;
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, w, h);
        ctx.restore();
      }
    }
  }
}

async function loadOverlays(overlayClips, cache, onWarn) {
  const out = [];
  for (const ov of overlayClips || []) {
    const url = String(ov?.image_url || '').trim();
    if (!url || url.startsWith('blob:')) continue;
    try {
      const bitmap = await cache.acquireBitmap(url);
      const start = Math.max(0, Number(ov.start_time) || 0);
      out.push({
        ov,
        bitmap,
        start,
        end: start + Math.max(0.01, Number(ov.playback_seconds) || 0.01),
      });
    } catch {
      onWarn?.('A host overlay image could not be loaded and was skipped.');
    }
  }
  return out;
}

function drawOverlay(ctx, entry, t, w, h) {
  const { ov, bitmap, start } = entry;
  const layer = ov.layer && typeof ov.layer === 'object' ? ov.layer : {};
  const { ox, oy, ow, oh } = layerToOverlayBox(layer, w, h);
  let alpha = Math.max(0.05, Math.min(1, Number(ov.opacity ?? 1) || 1));
  const fade = Math.max(0, Math.min(1.2, Number(ov.animation_duration) || 0));
  if (fade >= 0.12) alpha *= Math.min(1, Math.max(0, (t - start) / fade));
  if (alpha <= 0.001) return;
  const fit = coverFitRect(bitmap.width, bitmap.height, ow, oh, null);
  ctx.save();
  ctx.globalAlpha = alpha;
  const rot = Number(layer.rotation) || 0;
  ctx.translate(ox + ow / 2, oy + oh / 2);
  if (rot) ctx.rotate((rot * Math.PI) / 180);
  ctx.translate(-ow / 2, -oh / 2);
  ctx.beginPath();
  ctx.rect(0, 0, ow, oh);
  ctx.clip();
  if (fit) ctx.drawImage(bitmap, fit.dx, fit.dy, fit.dw, fit.dh);
  ctx.restore();
}

/**
 * @param {object} opts
 * @param {object} opts.body - server export request body (clips, audio_clips, subtitle_*, vfx_effects, overlay_clips, …)
 * @param {string} [opts.filename]
 * @param {FileSystemFileHandle|null} [opts.fileHandle] - from {@link pickLocalExportFile}
 * @param {(p: { message: string, progress: number }) => void} [opts.onProgress]
 * @param {AbortSignal} [opts.signal]
 */
export async function exportTimelineLocally({
  body,
  filename = 'timeline-export',
  fileHandle = null,
  onProgress,
  signal,
}) {
  if (!localExportSupported()) {
    throw new Error('This browser cannot render video locally. Use Chrome, Edge, Safari 17+ or Firefox 130+, or use Export.');
  }
  const report = (message, progress) => onProgress?.({ message, progress });
  const warnings = new Set();
  const warn = (msg) => warnings.add(msg);

  const items = timelineItems(body || {});
  if (!items.length) throw new Error('No clips to export');

  const fps = EXPORT_FPS;
  const aspect = body.aspect_ratio || '16:9';
  const { width: W, height: H } = exportTargetSizeForAspect(aspect);
  const counts = items.map((it) => clipFrameCount(it, fps));
  const plans = buildTrackPlans(items, counts, fps);
  const total = outputFrameCount(plans);
  const totalSeconds = total / fps;
  const firstNeeded = clipFirstNeededFrame(plans);
  const lastNeeded = clipLastNeededFrame(plans);

  const quality = qualityFor(body.export_quality);
  const videoCodec = await getFirstEncodableVideoCodec(['avc', 'hevc', 'vp9', 'av1'], {
    width: W,
    height: H,
    quality,
    frameRate: fps,
  });
  if (!videoCodec) throw new Error('This browser has no usable video encoder for MP4.');

  const cache = new MediaCache();
  const pieces = audioPiecesForExport(body.audio_clips, totalSeconds);
  const audioCodec = pieces.length
    ? await getFirstEncodableAudioCodec(['aac', 'opus'], {
      numberOfChannels: MIX_CHANNELS,
      sampleRate: MIX_SAMPLE_RATE,
    })
    : null;
  if (pieces.length && !audioCodec) warn('This browser cannot encode audio, so the video is silent.');

  const writable = fileHandle ? discardableFileStream(await fileHandle.createWritable()) : null;
  const target = writable ? new StreamTarget(writable.stream, { chunked: true }) : new BufferTarget();
  const output = new Output({
    format: new Mp4OutputFormat({ fastStart: writable ? false : 'in-memory' }),
    target,
  });
  const frameCanvas = makeCanvas(W, H);
  const frameCtx = frameCanvas.getContext('2d', { alpha: false });
  const videoSource = new CanvasSource(frameCanvas, {
    codec: videoCodec,
    bitrate: quality,
    keyFrameInterval: 2,
  });
  output.addVideoTrack(videoSource, { frameRate: fps });
  let audioSource = null;
  let mixer = null;
  if (audioCodec) {
    audioSource = new AudioBufferSource({ codec: audioCodec, bitrate: QUALITY_HIGH });
    output.addAudioTrack(audioSource);
    mixer = new ChunkedAudioMixer(pieces, totalSeconds, createAudioDecoder(cache));
  }

  const sources = new Map();
  let finished = false;
  try {
    report('Loading media…', 0);
    const overlays = await loadOverlays(body.overlay_clips, cache, warn);
    const cues = captionCues(body, items, W, H, aspect);
    await preloadCaptionFonts(cues.map((c) => c.spec));
    if (signal?.aborted) throw abortError();
    const effects = (body.vfx_effects || []).map(deserializeEffectClip).filter(Boolean);
    const vfx = effects.length ? RenderGraph.fromClips(effects, { fps }) : null;
    const painter = new ClipPainter(items, counts, W, H, fps, body.export_framing);
    const layerA = makeCanvas(W, H);
    const layerB = makeCanvas(W, H);
    const ctxA = layerA.getContext('2d', { alpha: false });
    const ctxB = layerB.getContext('2d', { alpha: false });

    const retired = new Set();
    const source = (key) => {
      if (!sources.has(key)) {
        if (retired.has(key)) return Promise.resolve(null);
        sources.set(key, openClipSource(items[key], cache, fps).catch(() => {
          warn('Some clip media could not be loaded; those frames are black.');
          return null;
        }));
      }
      return sources.get(key);
    };
    const clipFrame = async (key, j) => {
      const src = await source(key);
      return src ? src.frame(j) : null;
    };

    await output.start();
    const step = Math.max(1, Math.round(fps / 2));
    const prefetchFrames = fps * 4;
    const upcoming = [...firstNeeded.entries()].sort((a, b) => a[1] - b[1]);
    let upcomingAt = 0;

    for (let idx = 0; idx < total; idx += 1) {
      if (signal?.aborted) throw abortError();
      const t = idx / fps;
      const op = resolveFrame(plans, idx);

      frameCtx.save();
      frameCtx.fillStyle = '#000';
      frameCtx.fillRect(0, 0, W, H);
      frameCtx.restore();

      if (op.op === 'clip') {
        painter.paint(ctxA, op.key, op.j, await clipFrame(op.key, op.j));
        frameCtx.drawImage(layerA, 0, 0);
      } else if (op.op === 'blend') {
        painter.paint(ctxA, op.prevKey, op.prevJ, await clipFrame(op.prevKey, op.prevJ));
        painter.paint(ctxB, op.currKey, op.currJ, await clipFrame(op.currKey, op.currJ));
        const styles = transitionPreviewLayerStyles(op.tid, op.progress);
        if (styles) {
          drawStyledLayer(frameCtx, layerA, W, H, styles.outgoing);
          drawStyledLayer(frameCtx, layerB, W, H, styles.incoming);
        } else {
          frameCtx.drawImage(layerB, 0, 0);
        }
      }

      if (vfx) {
        const out = vfx.renderFrame({ width: W, height: H, canvas: frameCanvas }, t, idx, { fps, mode: 'preview' });
        if (out?.canvas && out.canvas !== frameCanvas) frameCtx.drawImage(out.canvas, 0, 0, W, H);
      }
      for (const entry of overlays) {
        if (t >= entry.start && t < entry.end) drawOverlay(frameCtx, entry, t, W, H);
      }
      for (const cue of cues) {
        if (t >= cue.start && t < cue.end && cue.covered(t)) drawCaption(frameCtx, cue.spec);
      }

      await videoSource.add(t, 1 / fps);

      if (mixer) {
        for await (const buf of mixer.renderUntil(t + 1 / fps)) await audioSource.add(buf);
      }
      if (idx % step === 0) {
        for (const [key, p] of sources) {
          if ((lastNeeded.get(key) ?? -1) < idx) {
            sources.delete(key);
            retired.add(key);
            p.then((s) => s?.close()).catch(() => {});
          }
        }
        while (upcomingAt < upcoming.length && upcoming[upcomingAt][1] <= idx + prefetchFrames) {
          const [key] = upcoming[upcomingAt];
          upcomingAt += 1;
          if ((lastNeeded.get(key) ?? -1) >= idx) source(key);
        }
        report(`Rendering ${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')} of ${Math.floor(totalSeconds / 60)}:${String(Math.floor(totalSeconds % 60)).padStart(2, '0')}`, idx / total);
      }
    }

    if (mixer) {
      for await (const buf of mixer.renderUntil(Infinity)) await audioSource.add(buf);
    }
    report('Finalizing MP4…', 1);
    await output.finalize();
    finished = true;
  } catch (err) {
    if (writable) await writable.discard();
    if (!finished) await output.cancel().catch(() => {});
    throw err;
  } finally {
    for (const p of sources.values()) p.then((s) => s?.close()).catch(() => {});
    cache.close();
  }

  if (!writable) {
    downloadBlob(new Blob([target.buffer], { type: 'video/mp4' }), `${filename}.mp4`);
  }
  return { success: true, warnings: [...warnings] };
}
